import React, { FC, useEffect, useMemo } from 'react';
import { useField, useFormikContext } from 'formik';
import { BasicContainer, FlexCol, FlexRow, H2Text } from '@minecraft.atoms';
import { useTranslation } from '@blocs.i18n';
import { SelectInput, TextInput, normalizeFormikError } from '@minecraft.molecules';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { AddressSuggestion, AddressChange } from '@blocs.address-suggestion';
import { PaymentMethodFormType } from '../types';
import { PaywallFunnelEvents } from '../../usePaywallFunnelEvents';
import { useBillingPopup } from '../../BillingPopupContext';
import { useBillingStates } from './useBillingStates';
import { getBillingAddressFormConfig } from './shouldShowBillingStateField';
import { formatBillingPostalCode } from './formatBillingPostalCode';

export type BillingAddressFormLayoutProps = {
  isBillingAddressFieldEnabled?: boolean;
  onAddressLine1Change: (change: AddressChange) => void;
  funnelEvents?: PaywallFunnelEvents;
};

const requiredLabel = (label: string) => `${label} *`;

const ADDRESS_FIELD_NAMES = [
  'country',
  'address_line_1',
  'address_line_2',
  'city',
  'state_region',
  'postal_code',
] as const;

export const toSelectOptions = (options: Array<{ id: string; label: string }>) => [
  { value: '', label: '' },
  ...options.map((option) => ({ value: option.id, label: option.label })),
];

/* eslint-disable camelcase -- Recurly data-recurly field names */
const EMPTY_BILLING_ADDRESS_FIELDS = {
  address_line_1: '',
  address_line_2: '',
  city: '',
  state_region: '',
  postal_code: '',
};

const UNTOUCHED_BILLING_ADDRESS_FIELDS = {
  address_line_1: false,
  address_line_2: false,
  city: false,
  state_region: false,
  postal_code: false,
};
/* eslint-enable camelcase */

export const BillingAddressFormLayout: FC<BillingAddressFormLayoutProps> = ({
  isBillingAddressFieldEnabled,
  onAddressLine1Change,
  funnelEvents,
}) => {
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();
  const { values, setValues, setTouched, touched } = useFormikContext<PaymentMethodFormType>();
  const hasTouchedAddressField = ADDRESS_FIELD_NAMES.some((field) => touched[field]);
  useEffect(() => {
    if (hasTouchedAddressField) {
      funnelEvents?.trackBillingAddressFieldStarted();
    }
  }, [hasTouchedAddressField, funnelEvents]);
  const [{ value: addressLine1 }, addressLine1Meta] = useField<string>('address_line_1');
  const {
    countryOptions,
    billingCountriesLoading: countriesLoading,
    billingCountriesError: countriesError,
  } = useBillingPopup();
  const selectedCountry = countryOptions.find((option) => option.id === values.country);
  const formConfig = getBillingAddressFormConfig(values.country);
  const { stateOptions, statesLoading, statesError } = useBillingStates(
    formConfig.showState ? selectedCountry?.countryId : undefined
  );
  // address_line_1 is always required here, so (matching TextInput's convention) only surface its
  // error once the field has been touched, rather than immediately on every keystroke.
  const isAddressLine1Invalid = !!addressLine1Meta.error && !!addressLine1Meta.touched;
  const addressLine1Validation = isAddressLine1Invalid
    ? { valid: false, message: normalizeFormikError(addressLine1Meta.error) }
    : undefined;

  useEffect(() => {
    if (!isBillingAddressFieldEnabled) {
      return;
    }

    if (countriesError) {
      showMessage({
        color: 'danger',
        message: t('common:paywall.checkout.billingAddress.countriesLoadError'),
      });
    }
  }, [countriesError, isBillingAddressFieldEnabled, showMessage, t]);

  useEffect(() => {
    if (!isBillingAddressFieldEnabled) {
      return;
    }

    if (statesError) {
      showMessage({
        color: 'danger',
        message: t('common:paywall.checkout.billingAddress.statesLoadError'),
      });
    }
  }, [isBillingAddressFieldEnabled, showMessage, statesError, t]);

  const countrySelectOptions = useMemo(() => toSelectOptions(countryOptions), [countryOptions]);
  const stateSelectOptions = useMemo(() => toSelectOptions(stateOptions), [stateOptions]);

  if (!isBillingAddressFieldEnabled) {
    return null;
  }

  const cityField = (
    <BasicContainer w="full">
      <TextInput name="city" label={t('common:form.label.city')} inputProps={{ 'data-recurly': 'city' }} required />
    </BasicContainer>
  );

  const postalCodeField = (
    <BasicContainer w="full">
      <TextInput
        name="postal_code"
        label={t('common:paywall.checkout.billingAddress.zipCode')}
        formatValue={(value) => formatBillingPostalCode(value, values.country)}
        inputProps={{ 'data-recurly': 'postal_code' }}
        required
      />
    </BasicContainer>
  );

  return (
    <BasicContainer w="full">
      <FlexCol gap="4" w="full">
        <H2Text>{t('common:paywall.checkout.billingAddress.title')}</H2Text>
        <SelectInput
          name="country"
          label={requiredLabel(t('common:paywall.checkout.billingAddress.countryOrRegion'))}
          options={countrySelectOptions}
          disabled={countriesLoading}
          inputProps={{ 'data-recurly': 'country' }}
          // Defer select errors until blur so changing country does not flag the empty state field.
          validationRequiresTouched
          onChange={(country) => {
            setValues(
              {
                ...values,
                country,
                ...EMPTY_BILLING_ADDRESS_FIELDS,
              },
              false
            );
            setTouched({ ...touched, ...UNTOUCHED_BILLING_ADDRESS_FIELDS }, false);
          }}
        />
        <FlexCol key={values.country} gap="4" w="full">
          <AddressSuggestion
            name="address_line_1"
            region={values.country ?? ''}
            value={addressLine1}
            label={t('common:paywall.checkout.billingAddress.addressLine1')}
            required
            validation={addressLine1Validation}
            data-recurly="address1"
            onChange={onAddressLine1Change}
            placeholder=""
          />
          <TextInput
            name="address_line_2"
            label={t('common:paywall.checkout.billingAddress.addressLine2')}
            inputProps={{ 'data-recurly': 'address2', placeholder: 'Apt., suite, unit number, etc.' }}
          />
          {formConfig.showState && cityField}
          <FlexRow gap="4" wrap="nowrap" w="full">
            {formConfig.showState ? (
              <BasicContainer w="full">
                <SelectInput
                  name="state_region"
                  label={requiredLabel(t(formConfig.stateLabelKey))}
                  options={stateSelectOptions}
                  disabled={statesLoading}
                  inputProps={{ 'data-recurly': 'state' }}
                  validationRequiresTouched
                />
              </BasicContainer>
            ) : (
              cityField
            )}
            {postalCodeField}
          </FlexRow>
        </FlexCol>
      </FlexCol>
    </BasicContainer>
  );
};
